import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import LessonQuiz from './LessonQuiz';
import AuthModal from './AuthModal';
import { Dialog, DialogContent } from './ui/dialog';

/**
 * LessonWrapper - Wraps lesson pages with quiz functionality
 * Adds: Quiz button, PDF lock/unlock, lesson completion tracking
 */
const LessonWrapper = ({ 
  courseType, 
  lessonNumber, 
  children, 
  pdfUrl,
  pdfTitle = "Student Book"
}) => {
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStatus, setQuizStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const authContext = useContext(AuthContext);

  useEffect(() => {
    fetchQuizStatus();
  }, [courseType, lessonNumber]);

  const fetchQuizStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/quiz/${courseType}/lesson/${lessonNumber}/status`,
        { withCredentials: true }
      );
      setQuizStatus(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to fetch quiz status:', error);
      // Check if it's an auth error (401)
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
      }
      // Default to unlocked for lesson 1
      setQuizStatus({
        is_unlocked: lessonNumber === 1,
        quiz_passed: false,
        pdf_unlocked: false
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleTakeQuiz = () => {
    // Check if user is authenticated
    if (authContext?.user || isAuthenticated) {
      setShowQuiz(true);
    } else {
      // Show auth modal to prompt login
      setShowAuthModal(true);
    }
  };
  
  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setIsAuthenticated(true);
    // Re-fetch quiz status now that user is logged in
    fetchQuizStatus();
    // Open the quiz after a short delay
    setTimeout(() => setShowQuiz(true), 300);
  };

  const handleQuizPassed = (result) => {
    setQuizStatus(prev => ({
      ...prev,
      quiz_passed: true,
      pdf_unlocked: true
    }));
    setShowQuiz(false);
  };

  // Don't show anything extra while loading
  if (loading) {
    return children;
  }

  return (
    <>
      {children}
      
      {/* Fixed Quiz/PDF Control Bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.8) 100%)',
        backdropFilter: 'blur(10px)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '16px',
        zIndex: 40,
        borderTop: '2px solid rgba(255,255,255,0.1)'
      }}>
        {/* Quiz Status Badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          background: quizStatus?.quiz_passed 
            ? 'rgba(34, 197, 94, 0.2)' 
            : 'rgba(234, 179, 8, 0.2)',
          borderRadius: '20px',
          border: `1px solid ${quizStatus?.quiz_passed ? 'rgba(34, 197, 94, 0.5)' : 'rgba(234, 179, 8, 0.5)'}`
        }}>
          <span style={{ fontSize: '18px' }}>
            {quizStatus?.quiz_passed ? '✅' : '📝'}
          </span>
          <span style={{ 
            color: quizStatus?.quiz_passed ? '#22c55e' : '#eab308',
            fontWeight: '600',
            fontSize: '14px'
          }}>
            {quizStatus?.quiz_passed ? 'Quiz Passed!' : 'Quiz Required'}
          </span>
        </div>

        {/* Take Quiz Button */}
        {!quizStatus?.quiz_passed && (
          <button
            onClick={handleTakeQuiz}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
              animation: 'pulse 2s infinite'
            }}
          >
            📝 Pass Quiz to Unlock Next Lesson
          </button>
        )}

        {/* Next Lesson Indicator */}
        {quizStatus?.quiz_passed && lessonNumber < 10 && (
          <div style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
            color: 'white',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(6, 182, 212, 0.4)'
          }}>
            🚀 Lesson {lessonNumber + 1} Unlocked!
          </div>
        )}
      </div>

      {/* Quiz Modal */}
      {showQuiz && (
        <LessonQuiz
          courseType={courseType}
          lessonNumber={lessonNumber}
          onQuizPassed={handleQuizPassed}
          onClose={() => setShowQuiz(false)}
        />
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `}</style>
      
      {/* Auth Modal for login prompt */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="sm:max-w-md">
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={handleAuthSuccess}
            skipNavigate={true}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LessonWrapper;
